import { useEffect, useMemo, useState } from "react"
import { IconAlertTriangle, IconLoader2, IconWand } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import type { BookingDetails } from "@/app/booking-calendar/bookingCalendarApi"
import { bookingWhen } from "@/app/booking-calendar/components/AffectedBookingsList"
import { SITE_TIME_ZONE } from "@/app/booking-calendar/lib/siteTime"
import {
  type AutomaticReassignmentPreview,
  useLazyGetResolveOptionsQuery,
  usePreviewAutomaticResolutionMutation,
  useRunAutomaticResolutionMutation,
} from "../schedule-overrides/scheduleOverridesApi"
import { formatDateRange } from "../schedule-overrides/lib/overrideForm"
import { appointments, customerNames, describeResolveError, formatInstant } from "./automaticReassignment"
import { BookingMoveBatchProgress } from "./BookingMoveBatchProgress"
import { CalThrottledNotice } from "@/components/CalThrottledNotice"
import { isCalThrottled } from "@/lib/calThrottled"

/** The public booking window, for the copy when the options could not say. */
const DEFAULT_LOOKAHEAD_DAYS = 28

type Step =
  | { kind: "checking" }
  | { kind: "refused"; message: string }
  /** Cal throttled the options, the plan or the run (503 calThrottled, AB#514): nothing was queued. */
  | { kind: "throttled"; error: unknown }
  | { kind: "plan"; plan: AutomaticReassignmentPreview; lookaheadDays: number }
  | { kind: "starting"; plan: AutomaticReassignmentPreview; lookaheadDays: number }
  | { kind: "progress"; batchId: string; finished: boolean }

/**
 * Automatic reassignment of one override's affected bookings (AB#810): reads the server's options —
 * following a batch already under way, or saying why it cannot run — then the plan (per booking: where it would go, or why nowhere fits; the totals; the
 * dates searched), and on Confirm queues the batch and follows its progress to the end.
 *
 * Open while `overrideId` is set. `saved` is the resolution choice's case: the override has just been
 * created, so the copy says that backing out leaves its appointments booked (as "Proceed without
 * rescheduling" does). `bookings` name the customers; the plan and the batch carry only booking uids.
 */
export function AutomaticReassignmentDialog({
  overrideId,
  bookings,
  saved = false,
  onClose,
  pollMs,
}: {
  overrideId: string | null
  bookings?: readonly BookingDetails[] | null
  saved?: boolean
  onClose: () => void
  /** For tests; the batch hook's default otherwise. */
  pollMs?: number
}) {
  const [step, setStep] = useState<Step>({ kind: "checking" })
  // Bumped by the busy notice's Retry: the options and the plan are read again from the start, so a
  // run Cal throttled is confirmed afresh rather than resent behind the reader's back.
  const [attempt, setAttempt] = useState(0)
  const [readOptions] = useLazyGetResolveOptionsQuery()
  const [preview] = usePreviewAutomaticResolutionMutation()
  const [run] = useRunAutomaticResolutionMutation()
  const names = useMemo(() => customerNames(bookings), [bookings])

  // Each time it opens: the options afresh, then the plan.
  useEffect(() => {
    if (!overrideId) return
    let cancelled = false
    setStep({ kind: "checking" })

    void (async () => {
      try {
        const options = await readOptions(overrideId, false).unwrap()
        if (cancelled) return
        if (options.activeBatchId) {
          setStep({ kind: "progress", batchId: options.activeBatchId, finished: false })
          return
        }
        if (!options.automatic.enabled) {
          setStep({ kind: "refused", message: options.automatic.reason ?? "Automatic reassignment is not available for this override." })
          return
        }
        const plan = await preview(overrideId).unwrap()
        if (!cancelled) setStep({ kind: "plan", plan, lookaheadDays: options.lookaheadDays || DEFAULT_LOOKAHEAD_DAYS })
      } catch (err) {
        if (!cancelled) setStep(failedStep(err))
      }
    })()

    return () => {
      cancelled = true
    }
  }, [overrideId, readOptions, preview, attempt])

  async function confirm() {
    if (!overrideId || step.kind !== "plan") return
    setStep({ ...step, kind: "starting" })
    try {
      const accepted = await run(overrideId).unwrap()
      setStep({ kind: "progress", batchId: accepted.batchId, finished: false })
    } catch (err) {
      setStep(failedStep(err))
    }
  }

  const starting = step.kind === "starting"
  const leftBooked = saved
    ? "The override is saved; its appointments stay booked, as with Proceed without rescheduling. They stay listed in its detail."
    : "Nothing has changed; the appointments stay booked."

  return (
    <Dialog open={overrideId !== null} onOpenChange={(next) => !next && !starting && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="automatic-reassignment">
        <DialogHeader>
          <DialogTitle>Automatic reassignment</DialogTitle>
          <DialogDescription>
            {saved ? "The override is saved. " : ""}
            Each appointment keeps its host, site, type and time of day, and moves to the earliest weekday past the booking window
            when that host is free.
          </DialogDescription>
        </DialogHeader>

        {step.kind === "checking" && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
            <IconLoader2 className="size-4 animate-spin" />
            Working out where each appointment can go…
          </div>
        )}

        {step.kind === "refused" && (
          <div className="flex flex-col gap-2 text-sm" data-testid="automatic-reassignment-refused">
            <p className="flex items-start gap-2 text-destructive" role="alert">
              <IconAlertTriangle className="mt-0.5 size-4 shrink-0" />
              {step.message}
            </p>
            <p className="text-muted-foreground">{leftBooked}</p>
          </div>
        )}

        {step.kind === "throttled" && (
          <div className="flex flex-col gap-2 text-sm" data-testid="automatic-reassignment-throttled">
            <CalThrottledNotice error={step.error} onRetry={() => setAttempt(n => n + 1)} />
            <p className="text-muted-foreground">{leftBooked}</p>
          </div>
        )}

        {(step.kind === "plan" || step.kind === "starting") && (
          <div className="flex flex-col gap-3 text-sm" data-testid="automatic-reassignment-plan">
            <p className="font-medium" data-testid="automatic-reassignment-totals">
              {step.plan.planned === 0 ? "No appointment can be moved" : `${appointments(step.plan.planned)} can be moved`}
              {step.plan.unplaceable > 0 && `; ${appointments(step.plan.unplaceable)} cannot and will be sent to the review queue`}.
            </p>
            <p className="text-muted-foreground">
              Searched {formatDateRange(step.plan.firstDate, step.plan.lastDate)}: from the first day past the {step.lookaheadDays}-day
              booking window, {step.plan.horizonDays} days on.
              {step.plan.unplaceable > 0 && " Those with no date stay booked where they are, waiting in Appointments → Booking Review."}
            </p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Appointment</TableHead>
                  <TableHead>Now</TableHead>
                  <TableHead>Moves to</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {step.plan.items.map(item => {
                  const now = bookingWhen({ start: item.currentStart, end: item.currentEnd }, SITE_TIME_ZONE)
                  return (
                    <TableRow key={item.requestId} data-plan-item={item.targetStart ? "planned" : "unplaceable"}>
                      <TableCell>{names.get(item.calBookingUid) ?? "Appointment"}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div>{now.date}</div>
                        <div className="text-xs text-muted-foreground">{now.time}</div>
                      </TableCell>
                      <TableCell>
                        {item.targetStart ? (
                          formatInstant(item.targetStart)
                        ) : (
                          <span className="text-amber-700 dark:text-amber-400">
                            {item.reason ?? "No date fits."} Sent to the review queue.
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
            <p className="text-xs text-muted-foreground">
              Each household moved is sent the usual reschedule notice. Cancel to leave them as they are. {leftBooked}
            </p>
          </div>
        )}

        {step.kind === "progress" && (
          <div className="flex flex-col gap-2">
            <BookingMoveBatchProgress
              batchId={step.batchId}
              names={names}
              pollMs={pollMs}
              onFinished={() => setStep(current => (current.kind === "progress" ? { ...current, finished: true } : current))}
            />
            {!step.finished && (
              <p className="text-xs text-muted-foreground">It carries on if you close this; you are notified when it finishes.</p>
            )}
          </div>
        )}

        <DialogFooter>
          {step.kind === "plan" || step.kind === "starting" ? (
            <>
              <Button variant="outline" onClick={onClose} disabled={starting}>
                {saved ? "Cancel, leave them booked" : "Cancel"}
              </Button>
              <Button onClick={() => void confirm()} disabled={starting || step.plan.items.length === 0}>
                {starting ? <IconLoader2 className="size-4 animate-spin" /> : <IconWand className="size-4" />}
                Confirm
              </Button>
            </>
          ) : (
            <Button variant={step.kind === "progress" && step.finished ? "default" : "outline"} onClick={onClose}>
              {step.kind === "progress" && step.finished ? "Done" : "Close"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** A failed options, preview or run request as the step it leaves the dialog on. */
function failedStep(err: unknown): Step {
  return isCalThrottled(err) ? { kind: "throttled", error: err } : { kind: "refused", message: describeResolveError(err) }
}
